import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { ExternalLink } from "lucide-react";
import PageCTA from "@/components/PageCTA";
import { Reveal, MaskedLine, HighlandBackdrop } from "@/components/Reveal";

const FILTERS = ["All", "Highlands", "Islands", "Cities"];

/* Curated region-appropriate fallback imagery for cards without a custom image */
const REGION_IMAGE = {
    Highlands: "https://images.pexels.com/photos/29353711/pexels-photo-29353711.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400",
    Islands: "https://images.pexels.com/photos/10677765/pexels-photo-10677765.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400",
    Cities: "https://images.pexels.com/photos/16948691/pexels-photo-16948691.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400",
};

const easeOut = [0.16, 1, 0.3, 1];

export default function Things() {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("All");

    useEffect(() => {
        api.get("/things").then((r) => setItems(r.data));
    }, []);

    const shown = filter === "All" ? items : items.filter((i) => i.region === filter);

    return (
        <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-24 lg:py-32" data-testid="things-page">
            <header className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16 relative">
                <div className="lg:col-span-8">
                    <Reveal>
                        <div className="overline mb-5"><span className="editorial-rule" />Plan your own flight</div>
                    </Reveal>
                    <h1
                        className="font-serif font-light tracking-tighter leading-[0.9]"
                        style={{ fontSize: "clamp(2.8rem, 7vw, 7rem)" }}
                        data-testid="things-title"
                    >
                        <div><MaskedLine delay={0.1}>Things worth</MaskedLine></div>
                        <div>
                            <MaskedLine delay={0.3}>
                                <span className="italic text-[var(--sb-brand)]">doing.</span>
                            </MaskedLine>
                        </div>
                    </h1>
                </div>
                <Reveal delay={0.4} className="lg:col-span-4 self-end">
                    <p className="text-[var(--sb-text-dim)] text-lg leading-relaxed">
                        A curated list of places, walks and quiet festivals that pair with the footage. Mostly off the package-tour radar.
                    </p>
                </Reveal>
            </header>

            <Reveal>
                <div className="flex gap-2 mb-16 flex-wrap" data-testid="things-filter">
                    {FILTERS.map((f) => (
                        <button
                            key={f}
                            onClick={() => setFilter(f)}
                            className={`px-5 py-2 text-xs tracking-[0.22em] uppercase border transition-colors ${
                                filter === f
                                    ? "bg-[var(--sb-brand)] text-[var(--sb-bg)] border-[var(--sb-brand)]"
                                    : "border-[var(--sb-border)] text-[var(--sb-text-dim)] hover:border-[var(--sb-brand)]"
                            }`}
                            data-testid={`things-filter-${f.toLowerCase()}`}
                        >
                            {f}
                        </button>
                    ))}
                </div>
            </Reveal>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8" data-testid="things-grid">
                {shown.map((t, i) => {
                    const img = t.image_url || REGION_IMAGE[t.region] || REGION_IMAGE.Highlands;
                    return (
                        <motion.a
                            key={t.id}
                            href={t.link}
                            target="_blank"
                            rel="noreferrer"
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-10% 0px" }}
                            transition={{ duration: 0.7, delay: (i % 4) * 0.06, ease: easeOut }}
                            whileHover={{ y: -6 }}
                            className="group relative block overflow-hidden border border-[var(--sb-border)] hover:border-[var(--sb-brand)] transition-[border-color,box-shadow] duration-500 hover:shadow-[0_20px_60px_-20px_rgba(107,167,222,0.35)]"
                            data-testid={`thing-${t.id}`}
                        >
                            {/* Image top */}
                            <div className="relative aspect-[16/10] overflow-hidden">
                                <img
                                    src={img}
                                    alt={t.title}
                                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.08]"
                                    loading="lazy"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-[var(--sb-bg)] via-[var(--sb-bg)]/40 to-transparent" />
                                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: "radial-gradient(50% 55% at 50% 55%, rgba(107,167,222,0.18) 0%, rgba(0,0,0,0) 65%)" }} />
                                <div className="absolute top-4 left-4 right-4 flex items-start justify-between">
                                    <span className="text-[10px] tracking-[0.28em] uppercase text-[var(--sb-text)] bg-[var(--sb-bg)]/50 backdrop-blur-sm border border-white/15 px-3 py-1.5">
                                        {t.region}
                                    </span>
                                    <ExternalLink size={16} className="text-[var(--sb-text)] group-hover:text-[var(--sb-brand)] transition-colors mt-1" />
                                </div>
                            </div>
                            {/* Content bottom */}
                            <div className="p-7 lg:p-8">
                                <h3
                                    className="font-serif text-3xl lg:text-4xl tracking-tight leading-[1.05] mb-4 transition-[color,transform] duration-500 group-hover:text-[var(--sb-brand)] group-hover:-translate-y-0.5"
                                >
                                    {t.title}
                                </h3>
                                <p className="text-[var(--sb-text-dim)] leading-relaxed">{t.summary}</p>
                                <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[var(--sb-brand)] mt-6 group-hover:gap-3 transition-all">
                                    Visit site <ExternalLink size={12} />
                                </span>
                            </div>
                        </motion.a>
                    );
                })}
            </div>
            <PageCTA variant="flight" className="!px-0" />
            <HighlandBackdrop variant="soft" />
        </div>
    );
}
